/**
 * Addenda for a locked Progress Note.
 *
 * Reads come straight from Medplum (the same way the locked viewer already
 * reads its QuestionnaireResponse and Claim) — only the two writes go through
 * the middleware, which owns the signature and the immutability guard.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMedplum } from '@medplum/react'
import { showNotification } from '@mantine/notifications'
import type { DocumentReference, Practitioner, Reference } from '@medplum/fhirtypes'
import type { MedplumClient } from '@medplum/core'
import type { OrpcClient } from '@/orpc/client'
import { useClient } from '@/hooks/useClient'
import {
  ADDENDUM_REASON_EXTENSION_URL,
  BEHUMAN_DOCUMENT_TYPE_SYSTEM,
  PROGRESS_NOTE_ADDENDUM_DOC_TYPE_CODE,
} from '@/model/middleware-types'
import { logError } from '@/utils/errorUtils'

export type AddendumReason = 'correction' | 'clarification' | 'additional-information' | 'other'

export const ADDENDUM_REASON_LABELS: Record<AddendumReason, string> = {
  correction: 'Correction',
  clarification: 'Clarification',
  'additional-information': 'Additional Information',
  other: 'Other',
}

export type NoteAddendum = {
  id: string
  content: string
  reason: AddendumReason
  signed: boolean
  author: string
  /** Who signed it — the addendum's electronic signature reads their profile (PD-1897). */
  signer?: Reference<Practitioner>
  date?: string
}

const addendaQueryKey = (documentReferenceId: string | undefined) =>
  ['note-addenda', { documentReferenceId }] as const

// `atob` yields a binary string; the note may contain non-ASCII, so decode the
// bytes rather than the characters.
function decodeContent(base64: string | undefined): string {
  if (!base64) return ''
  try {
    return new TextDecoder().decode(Uint8Array.from(atob(base64), (char) => char.charCodeAt(0)))
  } catch (err) {
    logError('progress-note-viewer', 'Failed to decode addendum content', err)
    return ''
  }
}

function toAddendum(docRef: DocumentReference): NoteAddendum {
  const reason = docRef.extension?.find((ext) => ext.url === ADDENDUM_REASON_EXTENSION_URL)
    ?.valueCode as AddendumReason | undefined

  return {
    id: docRef.id as string,
    content: decodeContent(docRef.content?.[0]?.attachment?.data),
    reason: reason ?? 'other',
    signed: docRef.docStatus === 'final',
    author: docRef.authenticator?.display || docRef.author?.[0]?.display || '—',
    signer: docRef.authenticator?.reference?.startsWith('Practitioner/')
      ? (docRef.authenticator as Reference<Practitioner>)
      : undefined,
    date: docRef.date,
  }
}

/** Shared by the viewer hook and the PDF export, which has no hook context. */
export async function fetchNoteAddenda(
  medplum: MedplumClient,
  documentReferenceId: string,
): Promise<NoteAddendum[]> {
  const typeToken = encodeURIComponent(
    `${BEHUMAN_DOCUMENT_TYPE_SYSTEM}|${PROGRESS_NOTE_ADDENDUM_DOC_TYPE_CODE}`,
  )
  const results = await medplum.searchResources(
    'DocumentReference',
    `type=${typeToken}&relatesto=DocumentReference/${documentReferenceId}&status=current&_sort=date&_count=50`,
  )
  return results.map(toAddendum)
}

export function useNoteAddenda(documentReferenceId: string | undefined) {
  const medplum = useMedplum()
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: addendaQueryKey(documentReferenceId),
    enabled: !!documentReferenceId,
    queryFn: () => fetchNoteAddenda(medplum, documentReferenceId!),
  })

  const addenda = query.data ?? []

  return {
    signedAddenda: addenda.filter((item) => item.signed),
    // Only one draft is offered at a time — the editor reopens the existing one.
    draft: addenda.find((item) => !item.signed),
    isLoading: query.isLoading,
    invalidate: () => {
      // MedplumClient caches identical searches — without dropping its cache
      // the refetch after save/sign returns the stale bundle.
      medplum.invalidateSearches('DocumentReference')
      return queryClient.invalidateQueries({ queryKey: addendaQueryKey(documentReferenceId) })
    },
  }
}

type SaveInput = {
  documentReferenceId: string
  addendumId?: string
  content: string
  reason: AddendumReason
}

export function useSaveAddendum(onSaved: () => void) {
  const client = useClient()

  return useMutation<
    Awaited<ReturnType<OrpcClient['progressNoteSaveAddendum']>>,
    unknown,
    SaveInput
  >({
    mutationFn: (input) => client.progressNoteSaveAddendum(input),
    onSuccess: () => {
      onSaved()
      showNotification({ color: 'green', message: 'Addendum draft saved.' })
    },
    onError: (err) => {
      logError('progress-note-viewer', 'Save addendum failed', err)
      showNotification({
        color: 'red',
        title: 'Could not save',
        message: 'The addendum draft was not saved. Please try again.',
      })
    },
  })
}

export function useSignAddendum(onSigned: () => void) {
  const client = useClient()

  return useMutation<
    Awaited<ReturnType<OrpcClient['progressNoteSignAddendum']>>,
    unknown,
    { addendumId: string }
  >({
    mutationFn: ({ addendumId }) => client.progressNoteSignAddendum({ addendumId, attested: true }),
    onSuccess: (data) => {
      onSigned()
      showNotification(
        data.alreadySigned
          ? { color: 'blue', message: 'This addendum was already signed.' }
          : { color: 'green', title: 'Addendum signed', message: 'It is now part of the record.' },
      )
    },
    onError: (err) => {
      logError('progress-note-viewer', 'Sign addendum failed', err)
      showNotification({
        color: 'red',
        title: 'Could not sign',
        message: 'The addendum was not signed. Please try again.',
      })
    },
  })
}
